import React from "react";
import { ShieldCheck, ShieldAlert, ShieldOff, ChevronRight } from "lucide-react";

/**
 * BLOCCO 4: Badge visivo della verifica S/MIME della firma tecnica di una PEC.
 *
 * Props:
 *   signatureVerification: object | null  (from `signature_verification` top-level)
 *
 * Rendering rules (contratto BLOCCO 3):
 *  - null                                → grigio "Firma non presente"
 *  - signature_chain_verified === true   → verde "Firma tecnica verificata"
 *  - signature_chain_verified === false  → rosso "Firma tecnica non verificata"
 */
export default function SignatureVerificationBadge({ signatureVerification }) {
    const sv = signatureVerification;

    if (sv === null || sv === undefined) {
        return (
            <div
                data-testid="signature-verification-badge"
                data-status="absent"
                className="inline-flex flex-col gap-1 rounded-xl border border-[#DCE3EE] bg-[#F5F7FB] px-4 py-3"
            >
                <div className="flex items-center gap-2">
                    <ShieldOff className="w-4 h-4 text-[#64748B]" strokeWidth={2} />
                    <span className="text-sm font-semibold text-[#64748B]">Firma non presente</span>
                </div>
                <div className="text-[11px] text-[#64748B] leading-tight">
                    Nessuna firma S/MIME rilevata sul messaggio.
                </div>
            </div>
        );
    }

    const verified = sv.signature_chain_verified === true;
    const gestore = sv.signer_gestore_pec || null;
    const anchorLabelMap = {
        agid_tsl: "AgID Trust List",
        test_ca: "CA di test",
        pec_providers: "Gestori PEC",
    };
    const anchor = sv.trust_anchor_source ? (anchorLabelMap[sv.trust_anchor_source] || sv.trust_anchor_source) : null;
    const matchedFrom = sv.signer_gestore_matched_from || "unknown";
    const matchedLabel = anchorLabelMap[matchedFrom] || "sconosciuto";

    if (verified) {
        return (
            <div
                data-testid="signature-verification-badge"
                data-status="verified"
                className="inline-flex flex-col gap-1 rounded-xl border border-[#16A34A]/30 bg-[#16A34A]/5 px-4 py-3 min-w-[280px]"
            >
                <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#16A34A]" strokeWidth={2.25} />
                    <span className="text-sm font-semibold text-[#166534]">Firma tecnica verificata</span>
                </div>
                {(gestore || anchor) && (
                    <div className="text-[11px] text-[#166534]/80 leading-tight flex items-center gap-1 flex-wrap">
                        {gestore && <span data-testid="signature-gestore" className="font-medium">{gestore}</span>}
                        {gestore && anchor && <ChevronRight className="w-3 h-3 opacity-60" />}
                        {anchor && (
                            <span data-testid="signature-anchor">
                                {anchor}
                                {matchedFrom && matchedFrom !== sv.trust_anchor_source && (
                                    <span className="opacity-70"> · match via {matchedLabel}</span>
                                )}
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            data-testid="signature-verification-badge"
            data-status="unverified"
            className="inline-flex flex-col gap-1 rounded-xl border border-[#DC2626]/30 bg-[#DC2626]/5 px-4 py-3 min-w-[280px]"
        >
            <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-[#DC2626]" strokeWidth={2.25} />
                <span className="text-sm font-semibold text-[#991B1B]">Firma tecnica non verificata</span>
            </div>
            {(gestore || anchor) && (
                <div className="text-[11px] text-[#991B1B]/80 leading-tight flex items-center gap-1 flex-wrap">
                    {gestore && <span data-testid="signature-gestore">{gestore}</span>}
                    {gestore && anchor && <ChevronRight className="w-3 h-3 opacity-60" />}
                    {anchor && <span data-testid="signature-anchor">{anchor}</span>}
                    {!anchor && <span className="opacity-80">Trust anchor non stabilito</span>}
                </div>
            )}
            {sv.certificate_revocation_status && sv.certificate_revocation_status !== "not_checked" && (
                <div className="text-[10px] text-[#991B1B]/70">
                    Revoca certificato: {sv.certificate_revocation_status}
                </div>
            )}
        </div>
    );
}
